const findPreviousValidValue = (
  data: Array<number | null>,
  currentIndex: number
): { index: number; value: number | null } => {
  let index = currentIndex - 1;
  while (index >= 0 && (data[index] === null || data[index] === undefined)) {
    index--;
  }

  return {
    index,
    value: index >= 0 ? data[index] : null,
  };
};

const findNextValidValue = (
  data: Array<number | null>,
  currentIndex: number
): { index: number; value: number | null } => {
  let index = currentIndex + 1;
  while (
    index < data.length &&
    (data[index] === null || data[index] === undefined)
  ) {
    index++;
  }

  return {
    index,
    value: index < data.length ? data[index] : null,
  };
};

export const interpolateNullValues = (
  data: Array<number | null>,
  currentWeekIndex?: number
): {
  interpolatedData: Array<number | null>;
  hiddenMarkerIndices: Set<number>;
  originalNullIndices: Set<number>;
  firstValidIndex: number | null;
} => {
  const interpolatedData: Array<number | null> = [];
  const hiddenMarkerIndices = new Set<number>();
  const originalNullIndices = new Set<number>();

  let firstValidIndex: number | null = null;
  for (let i = 0; i < data.length; i++) {
    if (data[i] !== null && data[i] !== undefined) {
      firstValidIndex = i;
      break;
    }
  }

  const maxInterpolationIndex =
    currentWeekIndex !== undefined && currentWeekIndex >= 0
      ? currentWeekIndex
      : data.length - 1;

  for (let i = 0; i < data.length; i++) {
    const value = data[i];

    if (value !== null && value !== undefined) {
      interpolatedData.push(value);
      continue;
    }

    originalNullIndices.add(i);

    if (i > maxInterpolationIndex) {
      interpolatedData.push(null);
      continue;
    }

    const { index: prevIndex, value: prevValue } = findPreviousValidValue(
      data,
      i
    );
    const { index: nextIndex, value: nextValue } = findNextValidValue(data, i);

    if (
      currentWeekIndex !== undefined &&
      currentWeekIndex >= 0 &&
      nextIndex > currentWeekIndex
    ) {
      if (prevValue !== null && prevValue !== undefined) {
        interpolatedData.push(prevValue);
        hiddenMarkerIndices.add(i);
      } else {
        interpolatedData.push(null);
      }
      continue;
    }

    if (i === 0 && (prevValue === null || prevValue === undefined)) {
      interpolatedData.push(0);
      continue;
    }

    if (
      i > 0 &&
      data[0] === null &&
      firstValidIndex !== null &&
      i < firstValidIndex &&
      nextValue !== null &&
      nextValue !== undefined
    ) {
      const steps = firstValidIndex - 0;
      const currentStep = i - 0;
      const interpolatedValue = 0 + ((nextValue - 0) * currentStep) / steps;
      interpolatedData.push(interpolatedValue);
      hiddenMarkerIndices.add(i);
      continue;
    }

    if (
      prevValue !== null &&
      prevValue !== undefined &&
      nextValue !== null &&
      nextValue !== undefined
    ) {
      const steps = nextIndex - prevIndex;
      const currentStep = i - prevIndex;
      const interpolatedValue =
        prevValue + ((nextValue - prevValue) * currentStep) / steps;
      interpolatedData.push(interpolatedValue);
      hiddenMarkerIndices.add(i);
      continue;
    }

    const hasPrevValue = prevValue !== null && prevValue !== undefined;
    if (hasPrevValue) {
      interpolatedData.push(prevValue);
      hiddenMarkerIndices.add(i);
      continue;
    }

    const hasNextValue = nextValue !== null && nextValue !== undefined;
    if (
      hasNextValue &&
      (currentWeekIndex === undefined ||
        currentWeekIndex < 0 ||
        nextIndex <= currentWeekIndex)
    ) {
      interpolatedData.push(nextValue);
      hiddenMarkerIndices.add(i);
      continue;
    }

    if (
      currentWeekIndex !== undefined &&
      currentWeekIndex >= 0 &&
      i >= currentWeekIndex
    ) {
      interpolatedData.push(null);
      hiddenMarkerIndices.add(i);
      continue;
    }

    interpolatedData.push(0);
    hiddenMarkerIndices.add(i);
  }

  return {
    interpolatedData,
    hiddenMarkerIndices,
    originalNullIndices,
    firstValidIndex,
  };
};
